import React, { useCallback, useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';

import type { FlowComponentRenderProps, TippingComponentConfig } from '../types';
import ApruvTermsNotice from '../../components/common/ApruvTermsNotice';
import {
  centsToDollars,
  dollarsToCents,
  formatCurrency,
  getTipFeeBreakdown,
  isValidTipAmount,
} from '../../utils/tipFee';
import {
  confirmPublicStripeTipCheckout,
  createPublicStripeTipCheckout,
  getPublicStripeTipConfig,
  loadStripeJS,
  type StripeEmbeddedCheckout,
} from '../../services/cxPublicStripeTipService';
import TippingAmountSelector from './tipping/TippingAmountSelector';
import TippingFeeSummary from './tipping/TippingFeeSummary';
import TippingPaymentOptions from './tipping/TippingPaymentOptions';
import type { TippingPaymentOption, TippingValue } from './tipping/types';

function parseCustomAmount(value: string): number | undefined {
  const parsed = Number.parseFloat(value);
  return isValidTipAmount(parsed) ? parsed : undefined;
}

const defaultTippingValue: TippingValue = {
  coverProcessingFee: false,
  paymentOption: 'card',
};

const TippingBlock: React.FC<FlowComponentRenderProps<TippingComponentConfig>> = ({
  config,
  goNext,
  onAction,
  onInteract,
  setValue,
  value,
  values,
}) => {
  const [checkoutState, setCheckoutState] = useState<
    'selecting' | 'loading' | 'checkout' | 'confirming'
  >('selecting');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const checkoutMountRef = useRef<HTMLDivElement | null>(null);
  const checkoutRef = useRef<StripeEmbeddedCheckout | null>(null);
  const returnSessionHandled = useRef(false);
  const mounted = useRef(true);
  const checkoutOperation = useRef(0);
  const tippingValue = { ...defaultTippingValue, ...((value ?? {}) as TippingValue) };
  const hasCustomAmount = !!tippingValue.customAmount?.trim();
  const presetAmount = tippingValue.amount;
  const amount = hasCustomAmount
    ? parseCustomAmount(tippingValue.customAmount ?? '')
    : isValidTipAmount(presetAmount)
      ? presetAmount
      : undefined;
  const customAmountBelowMinimum =
    hasCustomAmount && !parseCustomAmount(tippingValue.customAmount ?? '');
  const feeBreakdown = getTipFeeBreakdown(amount ?? 0, !!tippingValue.coverProcessingFee);
  const amountLabel = formatCurrency(feeBreakdown.amountCents);
  const termsEnabled = config.termsEnabled !== false;
  const surveyCode = typeof values.cxSurveyCode === 'string' ? values.cxSurveyCode.trim() : '';

  const updateValue = (nextValue: TippingValue) => {
    const mergedValue = {
      ...defaultTippingValue,
      ...tippingValue,
      ...nextValue,
    };
    const hasNextCustomAmount = !!mergedValue.customAmount?.trim();
    const nextPresetAmount = mergedValue.amount;
    const nextAmount = hasNextCustomAmount
      ? (parseCustomAmount(mergedValue.customAmount ?? '') ?? 0)
      : isValidTipAmount(nextPresetAmount)
        ? nextPresetAmount
        : 0;
    const nextBreakdown = getTipFeeBreakdown(nextAmount, !!mergedValue.coverProcessingFee);

    setValue(config.id, {
      ...mergedValue,
      amount: centsToDollars(nextBreakdown.amountCents),
      processingFee: centsToDollars(nextBreakdown.processingFeeCents),
      technicianTip: centsToDollars(nextBreakdown.technicianTipCents),
      total: centsToDollars(nextBreakdown.customerTotalCents),
    });
  };

  const finishPaidSession = useCallback(
    async (sessionId: string, operation: number) => {
      if (!mounted.current || checkoutOperation.current !== operation) return;
      if (!surveyCode) throw new Error('This survey does not have a payment link.');
      setCheckoutError(null);
      setCheckoutState('confirming');
      const result = await confirmPublicStripeTipCheckout(surveyCode, sessionId);
      if (!mounted.current || checkoutOperation.current !== operation) return;
      setValue(config.id, {
        ...tippingValue,
        stripePaymentStatus: result.paymentStatus,
        stripeSessionId: result.sessionId,
        stripeTransactionStatus: result.transactionStatus,
      });
      const interaction = {
        componentId: config.id,
        type: 'tip' as const,
        value: tippingValue.amount ?? true,
      };
      if (onInteract) onInteract(interaction);
      else onAction?.(interaction);

      const returnUrl = new URL(window.location.href);
      if (returnUrl.searchParams.has('stripe_tip_session_id')) {
        returnUrl.searchParams.delete('stripe_tip_session_id');
        window.history.replaceState({}, '', returnUrl.toString());
      }
      if (!onInteract) goNext();
    },
    [config.id, goNext, onAction, onInteract, setValue, surveyCode, tippingValue]
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      returnSessionHandled.current = false;
      checkoutOperation.current += 1;
      checkoutRef.current?.destroy();
      checkoutRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!config.checkoutEnabled || !surveyCode || returnSessionHandled.current) return;
    const sessionId = new URL(window.location.href).searchParams.get('stripe_tip_session_id');
    if (!sessionId) return;

    returnSessionHandled.current = true;
    const operation = ++checkoutOperation.current;
    void finishPaidSession(sessionId, operation).catch((error: unknown) => {
      if (!mounted.current || checkoutOperation.current !== operation) return;
      setCheckoutState('selecting');
      setCheckoutError(
        error instanceof Error ? error.message : 'We could not confirm your tip. Please try again.'
      );
      returnSessionHandled.current = false;
    });
  }, [config.checkoutEnabled, finishPaidSession, surveyCode]);

  const startCheckout = async () => {
    if (!amount || !surveyCode) return;
    const operation = ++checkoutOperation.current;
    setCheckoutError(null);
    setCheckoutState('loading');

    try {
      const [stripeConfig, stripeFactory, checkoutSession] = await Promise.all([
        getPublicStripeTipConfig(surveyCode),
        loadStripeJS(),
        createPublicStripeTipCheckout(surveyCode, {
          coverProcessingFee: !!tippingValue.coverProcessingFee,
          idempotencyKey:
            typeof crypto.randomUUID === 'function'
              ? crypto.randomUUID()
              : `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`,
          paymentOption: tippingValue.paymentOption ?? 'card',
          selectedAmountCents: dollarsToCents(amount),
        }),
      ]);
      if (!mounted.current || checkoutOperation.current !== operation) return;
      const stripe = stripeFactory(stripeConfig.publishableKey, {
        stripeAccount: stripeConfig.connectedAccountId,
      });
      const checkout = await stripe.initEmbeddedCheckout({
        clientSecret: checkoutSession.clientSecret,
        onComplete: () => {
          void finishPaidSession(checkoutSession.sessionId, operation).catch((error: unknown) => {
            if (!mounted.current || checkoutOperation.current !== operation) return;
            setCheckoutState('selecting');
            setCheckoutError(
              error instanceof Error
                ? error.message
                : 'Your payment completed, but the tip could not be confirmed. Please try again.'
            );
          });
        },
      });

      if (!mounted.current || checkoutOperation.current !== operation) {
        checkout.destroy();
        return;
      }
      checkoutRef.current?.destroy();
      checkoutRef.current = checkout;
      setCheckoutState('checkout');
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      if (!mounted.current || checkoutOperation.current !== operation) return;
      if (!checkoutMountRef.current) throw new Error('Stripe checkout could not be displayed.');
      checkout.mount(checkoutMountRef.current);
    } catch (error: unknown) {
      if (!mounted.current || checkoutOperation.current !== operation) return;
      checkoutRef.current?.destroy();
      checkoutRef.current = null;
      setCheckoutState('selecting');
      setCheckoutError(
        error instanceof Error ? error.message : 'Stripe checkout could not be started.'
      );
    }
  };

  const skipTipping = () => {
    const interaction = { componentId: config.id, type: 'continue' as const, value: false };
    if (onInteract) onInteract(interaction);
    else {
      onAction?.(interaction);
      goNext();
    }
  };

  return (
    <Card
      sx={{
        bgcolor: 'transparent',
        border: 0,
        borderRadius: 0,
        boxShadow: 'none',
        color: 'var(--cx-brand-text)',
        p: 0,
      }}
    >
      <Stack spacing={1.5}>
        {checkoutError ? <Alert severity="error">{checkoutError}</Alert> : null}

        {checkoutState === 'loading' || checkoutState === 'confirming' ? (
          <Box
            sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 240 }}
          >
            <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
              <CircularProgress size={32} />
              <Box sx={{ color: 'var(--cx-brand-text-muted)', fontWeight: 700 }}>
                {checkoutState === 'confirming'
                  ? 'Confirming your tip…'
                  : 'Opening secure checkout…'}
              </Box>
            </Stack>
          </Box>
        ) : null}

        <Box sx={{ display: checkoutState === 'checkout' ? 'block' : 'none' }}>
          <Box ref={checkoutMountRef} sx={{ minHeight: 420 }} />
          <Button
            color="inherit"
            fullWidth
            onClick={() => {
              checkoutOperation.current += 1;
              checkoutRef.current?.destroy();
              checkoutRef.current = null;
              setCheckoutState('selecting');
            }}
            type="button"
            variant="text"
          >
            Change tip
          </Button>
        </Box>

        {checkoutState === 'selecting' ? (
          <>
            <TippingAmountSelector
              amount={amount}
              amountLabel={amountLabel}
              config={config}
              customAmount={tippingValue.customAmount}
              customAmountBelowMinimum={customAmountBelowMinimum}
              onChange={updateValue}
            />

            <TippingPaymentOptions
              onChange={(paymentOption: TippingPaymentOption) => updateValue({ paymentOption })}
              paymentOption={tippingValue.paymentOption ?? 'card'}
            />

            {termsEnabled ? (
              <ApruvTermsNotice
                linkLabel={config.termsLinkLabel}
                modalTitle={config.termsModalTitle}
                termsHtml={config.termsHtml}
                text={config.termsText}
              />
            ) : null}

            <TippingFeeSummary
              amountLabel={amountLabel}
              coverProcessingFee={!!tippingValue.coverProcessingFee}
              onCoverProcessingFeeChange={(coverProcessingFee) =>
                updateValue({ coverProcessingFee })
              }
              processingFeeLabel={formatCurrency(feeBreakdown.processingFeeCents)}
              technicianTipLabel={formatCurrency(feeBreakdown.technicianTipCents)}
              totalLabel={formatCurrency(feeBreakdown.customerTotalCents)}
            />

            {config.checkoutEnabled ? (
              <Stack spacing={0.75} sx={{ pt: 0.5 }}>
                <Button
                  disabled={!amount || !surveyCode}
                  fullWidth
                  onClick={() => void startCheckout()}
                  sx={{ minHeight: 48 }}
                  type="button"
                  variant="contained"
                >
                  {config.continueLabel ?? 'Continue'}
                </Button>
                <Button
                  color="inherit"
                  fullWidth
                  onClick={skipTipping}
                  type="button"
                  variant="text"
                >
                  {config.skipLabel ?? 'Skip tipping'}
                </Button>
              </Stack>
            ) : null}
          </>
        ) : null}
      </Stack>
    </Card>
  );
};

export default TippingBlock;
